import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLatest } from '../../hooks/useLatest';
import { FiPlus, FiEdit, FiTrash2, FiSearch, FiClipboard, FiEye, FiUsers } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { jobOpeningsAPI, companiesAPI, usersAPI, settingsAPI } from '../../services/api';
import Modal from '../../components/Common/Modal';
import ColumnCustomizer, { useColumnPreferences } from '../../components/Common/ColumnCustomizer';
import { UnifiedFilterHeaderCell } from '../../components/Common/UnifiedTableFilters';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { useCountry } from '../../context/CountryContext';
import {
  runTableFilters,
  hasActiveClientTableFilters,
  CLIENT_SIDE_TABLE_FILTER_FETCH_LIMIT
} from '../../utils/tableFilters';

const JOB_LIST_PAGE_SIZE = 50;

const JOB_TYPES = ['Internship', 'Alternance', 'Full-time', 'Part-time'];
const CONTRACT_TYPES = ['CDI', 'CDD', 'Alternance', 'Stage', 'Freelance', 'Other'];
const PLACEMENT_STAGE_OPTIONS = ['job_dating', 'ongoing_placement', 'active_search', 'priority_placement'];

const JobList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { canPerformAction } = useAuth();
  const { activeCountry } = useCountry();
  const isFranceCRM = String(activeCountry || '').toUpperCase() === 'FRANCE';
  const canCreateJobs = canPerformAction('jobs', 'create');
  const canUpdateJobs = canPerformAction('jobs', 'update');
  const canDeleteJobs = canPerformAction('jobs', 'delete');
  const canManageJobs = canUpdateJobs || canDeleteJobs;
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterContractType, setFilterContractType] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterCompanyId, setFilterCompanyId] = useState('');
  const [filterProgramType, setFilterProgramType] = useState('');
  const [filterAssignedManagerId, setFilterAssignedManagerId] = useState('');
  const [page, setPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [managers, setManagers] = useState([]);
  const [programTypes, setProgramTypes] = useState([]);
  const [showStudentStatsModal, setShowStudentStatsModal] = useState(false);
  const [studentStatsLoading, setStudentStatsLoading] = useState(false);
  const [selectedJobForStats, setSelectedJobForStats] = useState(null);
  const [studentStats, setStudentStats] = useState({ total: 0, byStatus: {} });
  const [jobDocs, setJobDocs] = useState([]);
  const [tableFilters, setTableFilters] = useState({ globalSearch: '', columns: {} });
  const jobTypeLabel = (v) => {
    const key = {
      'Full-time': 'jobs.types.fullTime',
      'Part-time': 'jobs.types.partTime',
      Internship: 'jobs.types.internship',
      Alternance: 'jobs.types.alternance'
    }[v];
    return key ? t(key) : v;
  };

  const placementStageLabel = (s) => {
    const key = {
      job_dating: 'companies.relationshipStages.jobDating',
      ongoing_placement: 'companies.relationshipStages.ongoingPlacement',
      active_search: 'companies.relationshipStages.activeSearch',
      priority_placement: 'companies.relationshipStages.priorityPlacement'
    }[s];
    return key ? t(key) : (s || '-');
  };
  const jobColumns = useMemo(() => ([
    { key: 'offer_title', label: t('jobs.offerTitle') },
    { key: 'number_of_positions', label: t('jobs.positions', 'Positions'), defaultVisible: false },
    { key: 'company', label: t('jobs.company') },
    { key: 'students_count', label: t('jobs.studentsAppliedCount', 'Students Applied'), locked: true },
    { key: 'program_type', label: t('settings.fields.programType', 'Program Type') },
    { key: 'job_type', label: t('jobs.jobType') },
    { key: 'contract_type', label: t('jobs.contractType') },
    { key: 'placement_stage', label: t('companies.relationshipStage', 'Placement Stage'), defaultVisible: false },
    { key: 'last_contact_date', label: t('jobs.lastContact'), defaultVisible: false },
    { key: 'next_followup_date', label: t('jobs.nextFollowup') },
    { key: 'assigned_manager', label: t('jobs.assignedManager') },
    { key: 'handover_manager', label: t('jobs.handoverManager'), defaultVisible: false },
    { key: 'email_stage', label: t('jobs.emailStage'), defaultVisible: false },
    { key: 'reminder_enabled', label: t('jobs.reminderEnabled'), defaultVisible: false },
    { key: 'created_by', label: t('common.createdBy', 'Created By'), defaultVisible: false },
    { key: 'updated_at', label: t('common.updatedAt', 'Updated At'), defaultVisible: false },
    { key: 'actions', label: t('common.actions'), locked: true }
  ]), [t]);
  const { isVisible, toggleColumn, resetToDefault, columns: colDefs } = useColumnPreferences('job_openings', jobColumns);
  const columnFilterDefs = useMemo(() => {
    const companyOptions = Array.from(new Set((jobs || []).map((j) => String(j.company_name || '')).filter(Boolean)));
    const assignedOptions = Array.from(new Set((jobs || []).map((j) => String(j.assigned_manager_name || '')).filter(Boolean)));
    const createdByOptions = Array.from(new Set((jobs || []).map((j) => String(j.created_by_name || '')).filter(Boolean)));
    return [
      { key: 'offer_title', label: t('jobs.offerTitle'), field: 'offer_title', type: 'text' },
      { key: 'number_of_positions', label: t('jobs.positions', 'Positions'), field: 'number_of_positions', type: 'text' },
      { key: 'company', label: t('jobs.company'), field: 'company_name', type: 'multi', options: companyOptions.map((v) => ({ value: v, label: v })) },
      { key: 'program_type', label: t('settings.fields.programType', 'Program Type'), field: 'program_type', type: 'text' },
      { key: 'job_type', label: t('jobs.jobType'), field: 'job_type', type: 'multi', options: JOB_TYPES.map((v) => ({ value: v, label: jobTypeLabel(v) })) },
      { key: 'contract_type', label: t('jobs.contractType'), field: 'contract_type', type: 'multi', options: CONTRACT_TYPES.map((v) => ({ value: v, label: v })) },
      { key: 'last_contact_date', label: t('jobs.lastContact'), field: 'last_contact_date', type: 'date' },
      { key: 'next_followup_date', label: t('jobs.nextFollowup'), field: 'next_followup_date', type: 'date' },
      { key: 'assigned_manager', label: t('jobs.assignedManager'), field: 'assigned_manager_name', type: 'multi', options: assignedOptions.map((v) => ({ value: v, label: v })) },
      { key: 'handover_manager', label: t('jobs.handoverManager'), field: 'handover_manager_name', type: 'text' },
      { key: 'email_stage', label: t('jobs.emailStage'), field: 'email_stage', type: 'text' },
      { key: 'created_by', label: t('common.createdBy', 'Created By'), field: 'created_by_name', type: 'multi', options: createdByOptions.map((v) => ({ value: v, label: v })) },
      { key: 'updated_at', label: t('common.updatedAt', 'Updated At'), field: 'updated_at', type: 'date' }
    ];
  }, [jobs, t]);
  const columnFilterDefsRef = useLatest(columnFilterDefs);
  const columnFilterMap = useMemo(() => Object.fromEntries(columnFilterDefs.map((d) => [d.key, d])), [columnFilterDefs]);
  const updateColumnFilters = useCallback((updater) => setTableFilters((prev) => ({ ...prev, columns: updater(prev.columns || {}) })), []);
  const filteredJobs = useMemo(
    () => runTableFilters(jobs, tableFilters, tableFilters, ['offer_title', 'company_name', 'program_type', 'assigned_manager_name', 'email_stage'], columnFilterDefs),
    [jobs, tableFilters, columnFilterDefs]
  );
  const clientTableFilterMode = useMemo(
    () => hasActiveClientTableFilters(tableFilters, columnFilterDefs),
    [tableFilters, columnFilterDefs]
  );
  const pagedFilteredJobs = useMemo(() => {
    if (!clientTableFilterMode) return filteredJobs;
    const start = (page - 1) * JOB_LIST_PAGE_SIZE;
    return filteredJobs.slice(start, start + JOB_LIST_PAGE_SIZE);
  }, [clientTableFilterMode, filteredJobs, page]);
  const listTotalForPaging = clientTableFilterMode ? filteredJobs.length : total;

  useEffect(() => {
    const tp = Math.max(1, Math.ceil(listTotalForPaging / JOB_LIST_PAGE_SIZE));
    if (page > tp) setPage(tp);
  }, [clientTableFilterMode, filteredJobs.length, total, page]);

  const emptyForm = {
    offer_title: '', job_type: 'Internship', responsibilities: '', contract_type: 'Alternance',
    program_type: 'All',
    number_of_positions: 1,
    exchange_history: '', last_contact_date: '', next_followup_date: '', followup_action: '',
    email_stage: '', reminder_enabled: false, company_id: '',
    assigned_manager_id: '', handover_manager_id: '', meeting_history: '',
    placement_stage: ''
  };
  const [form, setForm] = useState(emptyForm);

  const loadJobs = useCallback(async () => {
    setLoading(true);
    try {
      const active = clientTableFilterMode;
      const params = {
        limit: active ? CLIENT_SIDE_TABLE_FILTER_FETCH_LIMIT : JOB_LIST_PAGE_SIZE,
        page: active ? 1 : page,
        search: search || undefined,
        contract_type: filterContractType || undefined,
        job_type: filterType || undefined,
        company_id: filterCompanyId || undefined,
        program_type: filterProgramType || undefined,
        assigned_manager_id: filterAssignedManagerId || undefined
      };
      const res = await jobOpeningsAPI.getAll(params);
      setJobs(res.data.jobs || []);
      setTotal(res.data.total || 0);
    } catch { toast.error(t('jobs.failedLoad')); }
    setLoading(false);
  }, [search, filterContractType, filterType, filterCompanyId, filterProgramType, filterAssignedManagerId, page, t, clientTableFilterMode]);

  useEffect(() => { loadJobs(); }, [loadJobs]);

  useEffect(() => {
    companiesAPI.getAll({ limit: 500 }).then(r => setCompanies(r.data.companies || [])).catch(() => {});
    usersAPI.getSalesTeam().then(r => setManagers(r.data || [])).catch(() => {});
    settingsAPI.getProgramTypes()
      .then((r) => {
        const rows = Array.isArray(r?.data) ? r.data : [];
        const franceRows = rows.filter((p) => {
          const cc = String(p?.country_code || '').toUpperCase();
          const status = String(p?.status || 'active').toLowerCase();
          return status === 'active' && (!cc || cc === 'FRANCE');
        });
        setProgramTypes(franceRows);
      })
      .catch(() => setProgramTypes([]));
  }, []);

  useEffect(() => {
    if (!isFranceCRM) {
      setJobDocs([]);
      return;
    }
    settingsAPI
      .getJobDocuments()
      .then((r) => {
        const rows = Array.isArray(r?.data) ? r.data : [];
        setJobDocs(rows.filter((d) => String(d?.status || '').toLowerCase() === 'active'));
      })
      .catch(() => setJobDocs([]));
  }, [isFranceCRM]);

  const handleSubmit = async () => {
    if (!form.offer_title) { toast.error(t('jobs.titleRequired')); return; }
    if (!form.company_id) { toast.error(t('jobs.companyRequired', 'Company is required')); return; }
    try {
      const payload = {
        ...form,
        company_id: Number(form.company_id),
        number_of_positions: Math.max(1, Number(form.number_of_positions) || 1)
      };
      if (editing) {
        await jobOpeningsAPI.update(editing.job_id, payload);
        toast.success(t('jobs.updated'));
      } else {
        await jobOpeningsAPI.create(payload);
        toast.success(t('jobs.created'));
      }
      setShowModal(false); setEditing(null); setForm(emptyForm); loadJobs();
    } catch (err) { toast.error(err.response?.data?.message || t('common.failed')); }
  };

  const handleEdit = (j) => {
    setEditing(j);
    setForm({
      offer_title: j.offer_title || j.job_title || '',
      program_type: j.program_type || 'All',
      job_type: j.job_type || 'Internship',
      responsibilities: j.responsibilities || '',
      contract_type: j.contract_type || 'Alternance',
      exchange_history: j.exchange_history || '',
      last_contact_date: j.last_contact_date || '',
      next_followup_date: j.next_followup_date || '',
      followup_action: j.followup_action || '',
      email_stage: j.email_stage || '',
      reminder_enabled: Number(j.reminder_enabled) === 1,
      company_id: j.company_id || '',
      number_of_positions: j.number_of_positions ?? 1,
      assigned_manager_id: j.assigned_manager_id || '',
      handover_manager_id: j.handover_manager_id || '',
      meeting_history: j.meeting_history || '',
      placement_stage: j.placement_stage || ''
    });
    setShowModal(true);
  };

  const handleAdd = () => { setEditing(null); setForm(emptyForm); setShowModal(true); };

  const handleDelete = async (id) => {
    if (!window.confirm(t('jobs.deleteConfirm'))) return;
    try { await jobOpeningsAPI.delete(id); toast.success(t('jobs.deleted')); loadJobs(); }
    catch { toast.error(t('common.failed')); }
  };

  const totalPages = Math.max(1, Math.ceil(listTotalForPaging / JOB_LIST_PAGE_SIZE));

  const openStudentStats = async (job) => {
    setSelectedJobForStats(job);
    setShowStudentStatsModal(true);
    setStudentStatsLoading(true);
    try {
      const res = await jobOpeningsAPI.getStudents(job.job_id);
      const rows = Array.isArray(res?.data?.students) ? res.data.students : [];
      const byStatus = rows.reduce((acc, row) => {
        const key = String(row?.status || 'Unknown').trim() || 'Unknown';
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      }, {});
      setStudentStats({ total: rows.length, byStatus });
    } catch (_) {
      setStudentStats({ total: 0, byStatus: {} });
      toast.error(t('common.failed'));
    } finally {
      setStudentStatsLoading(false);
    }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h2><FiClipboard style={{ marginRight: 8 }} />{t('jobs.title')}</h2>
          <p className="page-subtitle">{t('jobs.openingsCount', { count: clientTableFilterMode ? listTotalForPaging : total })}</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {isFranceCRM && jobDocs.length > 0 && (
            <select
              value=""
              onChange={(e) => {
                const v = e.target.value;
                if (!v) return;
                const docId = Number(v);
                if (!docId) return;
                window.open(settingsAPI.downloadJobDocumentUrl(docId), '_blank', 'noopener,noreferrer');
                e.target.value = '';
              }}
              style={{ padding: '10px 12px', borderRadius: 10, border: '1px solid var(--border)', fontSize: 13, minWidth: 220 }}
              title="Download documents"
            >
              <option value="">Download documents…</option>
              {jobDocs.map((d) => (
                <option key={d.job_document_id} value={d.job_document_id}>
                  {d.document_name}
                </option>
              ))}
            </select>
          )}
          {canCreateJobs && <button className="btn btn-primary" onClick={handleAdd}><FiPlus /> {t('jobs.addBtn')}</button>}
        </div>
      </div>

      <div className="filter-bar" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
        <div className="search-box" style={{ flex: '1 1 200px' }}>
          <FiSearch />
          <input placeholder={t('jobs.searchPlaceholder')} value={tableFilters.globalSearch}
            onChange={(e) => { setTableFilters((prev) => ({ ...prev, globalSearch: e.target.value })); setPage(1); }} />
        </div>
        <select value={filterType} onChange={(e) => { setFilterType(e.target.value); setPage(1); }}
          style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13 }}>
          <option value="">{t('jobs.allTypes')}</option>
          {JOB_TYPES.map((jobType) => <option key={jobType} value={jobType}>{jobTypeLabel(jobType)}</option>)}
        </select>
        <select value={filterContractType} onChange={(e) => { setFilterContractType(e.target.value); setPage(1); }}
          style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13 }}>
          <option value="">{t('jobs.allContracts')}</option>
          {CONTRACT_TYPES.map((ct) => <option key={ct} value={ct}>{t(`jobs.contractType.${ct}`, { defaultValue: ct })}</option>)}
        </select>
        <select value={filterProgramType} onChange={(e) => { setFilterProgramType(e.target.value); setPage(1); }}
          style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13 }}>
          <option value="">{t('settings.fields.programType', 'Program Type')} ({t('common.all', 'All')})</option>
          <option value="All">{t('common.all', 'All')}</option>
          {programTypes.map((p) => (
            <option key={p.program_type_id || p.program_type_name} value={p.program_type_name}>
              {p.program_type_name}
            </option>
          ))}
        </select>
        <select value={filterCompanyId} onChange={(e) => { setFilterCompanyId(e.target.value); setPage(1); }}
          style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13 }}>
          <option value="">{t('jobs.company')} ({t('common.all', 'All')})</option>
          {companies.map((c) => <option key={c.company_id} value={c.company_id}>{c.company_name}</option>)}
        </select>
        <select value={filterAssignedManagerId} onChange={(e) => { setFilterAssignedManagerId(e.target.value); setPage(1); }}
          style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13 }}>
          <option value="">{t('jobs.assignedManager')} ({t('common.all', 'All')})</option>
          {managers.map((u) => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
        </select>
        <ColumnCustomizer
          columns={colDefs}
          isVisible={isVisible}
          toggleColumn={toggleColumn}
          resetToDefault={resetToDefault}
        />
      </div>

      {loading ? <div className="loading-spinner"><div className="spinner"></div></div> : (
        <div className="table-responsive">
          <table>
            <thead>
              <tr>
                {isVisible('offer_title') && <UnifiedFilterHeaderCell column={columnFilterMap.offer_title} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                {isVisible('number_of_positions') && <UnifiedFilterHeaderCell column={columnFilterMap.number_of_positions} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                {isVisible('company') && <UnifiedFilterHeaderCell column={columnFilterMap.company} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                <th>{t('jobs.studentsAppliedCount', 'Students Applied')}</th>
                {isVisible('program_type') && <UnifiedFilterHeaderCell column={columnFilterMap.program_type} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                {isVisible('job_type') && <UnifiedFilterHeaderCell column={columnFilterMap.job_type} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                {isVisible('contract_type') && <UnifiedFilterHeaderCell column={columnFilterMap.contract_type} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                {isVisible('last_contact_date') && <UnifiedFilterHeaderCell column={columnFilterMap.last_contact_date} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                {isVisible('next_followup_date') && <UnifiedFilterHeaderCell column={columnFilterMap.next_followup_date} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                {isVisible('assigned_manager') && <UnifiedFilterHeaderCell column={columnFilterMap.assigned_manager} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                {isVisible('handover_manager') && <UnifiedFilterHeaderCell column={columnFilterMap.handover_manager} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                {isVisible('email_stage') && <UnifiedFilterHeaderCell column={columnFilterMap.email_stage} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                {isVisible('reminder_enabled') && <th>{t('jobs.reminderEnabled')}</th>}
                {isVisible('created_by') && <UnifiedFilterHeaderCell column={columnFilterMap.created_by} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                {isVisible('updated_at') && <UnifiedFilterHeaderCell column={columnFilterMap.updated_at} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                <th>{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {pagedFilteredJobs.map((j) => (
                <tr key={j.job_id}>
                  {isVisible('offer_title') && (
                    <td>
                      <button
                        type="button"
                        className="btn-link"
                        style={{ padding: 0, border: 0, background: 'none', color: 'var(--primary)', cursor: 'pointer', fontWeight: 700 }}
                        onClick={() => navigate(`/jobs/${j.job_id}`)}
                        title={t('common.view', 'View')}
                      >
                        {j.offer_title || j.job_title || '-'}
                      </button>
                    </td>
                  )}
                  {isVisible('number_of_positions') && <td>{Number(j.number_of_positions || 1)}</td>}
                  {isVisible('company') && (
                    <td>
                      {j.company_id ? (
                        <button
                          type="button"
                          className="btn-link"
                          style={{ padding: 0, border: 0, background: 'none', color: 'var(--primary)', cursor: 'pointer', fontWeight: 600 }}
                          onClick={() => navigate(`/companies/${j.company_id}`)}
                        >
                          {j.company_name || '-'}
                        </button>
                      ) : (j.company_name || '-')}
                    </td>
                  )}
                  <td>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline"
                      onClick={() => openStudentStats(j)}
                      title={t('jobs.studentsAppliedCount', 'Students Applied')}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                    >
                      <FiUsers />
                      <span>{Number(j.total_students || 0)}</span>
                    </button>
                  </td>
                  {isVisible('program_type') && <td>{j.program_type || 'All'}</td>}
                  {isVisible('job_type') && <td>{jobTypeLabel(j.job_type) || '-'}</td>}
                  {isVisible('contract_type') && <td>{j.contract_type || '-'}</td>}
                  {isVisible('placement_stage') && <td>{placementStageLabel(j.placement_stage) || '-'}</td>}
                  {isVisible('last_contact_date') && <td>{j.last_contact_date || '-'}</td>}
                  {isVisible('next_followup_date') && <td>{j.next_followup_date || '-'}</td>}
                  {isVisible('assigned_manager') && <td>{j.assigned_manager_name || '-'}</td>}
                  {isVisible('handover_manager') && <td>{j.handover_manager_name || '-'}</td>}
                  {isVisible('email_stage') && <td>{j.email_stage || '-'}</td>}
                  {isVisible('reminder_enabled') && <td>{Number(j.reminder_enabled) === 1 ? t('common.yes') : t('common.no')}</td>}
                  {isVisible('created_by') && <td>{j.created_by_name || '-'}</td>}
                  {isVisible('updated_at') && <td>{j.updated_at ? new Date(j.updated_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : '-'}</td>}
                  <td>
                    <div className="table-actions">
                      <button className="view" title={t('common.view')} onClick={() => navigate(`/jobs/${j.job_id}`)}>
                        <FiEye />
                      </button>
                      {canUpdateJobs && (
                        <button className="edit" title={t('common.edit')} onClick={() => handleEdit(j)}>
                          <FiEdit />
                        </button>
                      )}
                      {canDeleteJobs && (
                        <button className="delete" title={t('common.delete')} onClick={() => handleDelete(j.job_id)}>
                          <FiTrash2 />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {filteredJobs.length === 0 && (
                <tr>
                  <td colSpan="15" className="text-center" style={{ padding: 20, color: 'var(--text-muted)' }}>
                    {t('jobs.noJobs')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
          <button className="btn btn-sm btn-outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>{t('common.prev')}</button>
          <span style={{ padding: '6px 12px', fontSize: 13 }}>{t('common.page', { current: page, total: totalPages })}</span>
          <button className="btn btn-sm btn-outline" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>{t('common.next')}</button>
        </div>
      )}

      <Modal isOpen={showModal} onClose={() => setShowModal(false)}
        title={editing ? t('jobs.editTitle') : t('jobs.addTitle')}
        footer={<>
          <button className="btn btn-outline" onClick={() => setShowModal(false)}>{t('common.cancel')}</button>
          <button className="btn btn-primary" onClick={handleSubmit}>{editing ? t('common.update') : t('common.create')}</button>
        </>}>
        <div className="form-grid">
          <div className="form-group full-width">
            <label>{t('jobs.offerTitle')} <span className="required">*</span></label>
            <input value={form.offer_title} onChange={e => setForm(f => ({ ...f, offer_title: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>{t('jobs.positions', 'Number of Positions')}</label>
            <input
              type="number"
              min="1"
              value={form.number_of_positions}
              onChange={e => setForm(f => ({ ...f, number_of_positions: e.target.value }))}
            />
          </div>
          <div className="form-group">
            <label>{t('jobs.company')} <span className="required">*</span></label>
            <select value={form.company_id} onChange={e => setForm(f => ({ ...f, company_id: e.target.value }))}>
              <option value="">{t('common.select')}</option>
              {companies.map(c => <option key={c.company_id} value={c.company_id}>{c.company_name}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>{t('settings.fields.programType', 'Program Type')}</label>
            <select value={form.program_type} onChange={e => setForm(f => ({ ...f, program_type: e.target.value }))}>
              <option value="All">All</option>
              {programTypes.map((p) => (
                <option key={p.program_type_id || p.program_type_name} value={p.program_type_name}>
                  {p.program_type_name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>{t('jobs.jobType')}</label>
            <select value={form.job_type} onChange={e => setForm(f => ({ ...f, job_type: e.target.value }))}>
              {JOB_TYPES.map((jobType) => <option key={jobType} value={jobType}>{jobTypeLabel(jobType)}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>{t('jobs.contractType')}</label>
            <select value={form.contract_type} onChange={e => setForm(f => ({ ...f, contract_type: e.target.value }))}>
              {CONTRACT_TYPES.map((ct) => <option key={ct} value={ct}>{t(`jobs.contractType.${ct}`, { defaultValue: ct })}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>{t('companies.relationshipStage', 'Placement Stage')}</label>
            <select value={form.placement_stage} onChange={e => setForm(f => ({ ...f, placement_stage: e.target.value }))}>
              <option value="">{t('common.select')}</option>
              {PLACEMENT_STAGE_OPTIONS.map((s) => (
                <option key={s} value={s}>{placementStageLabel(s)}</option>
              ))}
            </select>
          </div>
          <div className="form-group full-width">
            <label>{t('jobs.responsibilities')}</label>
            <textarea value={form.responsibilities} onChange={e => setForm(f => ({ ...f, responsibilities: e.target.value }))} rows={3} />
          </div>
          <div className="form-group full-width">
            <label>{t('jobs.exchangeHistory')}</label>
            <textarea value={form.exchange_history} onChange={e => setForm(f => ({ ...f, exchange_history: e.target.value }))} rows={3} />
          </div>
          <div className="form-group">
            <label>{t('jobs.lastContact')}</label>
            <input type="date" value={form.last_contact_date} onChange={e => setForm(f => ({ ...f, last_contact_date: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>{t('jobs.nextFollowup')}</label>
            <input type="date" value={form.next_followup_date} onChange={e => setForm(f => ({ ...f, next_followup_date: e.target.value }))} />
          </div>
          <div className="form-group full-width">
            <label>{t('jobs.followupAction')}</label>
            <textarea value={form.followup_action} onChange={e => setForm(f => ({ ...f, followup_action: e.target.value }))} rows={2} />
          </div>
          <div className="form-group">
            <label>{t('jobs.emailStage')}</label>
            <input value={form.email_stage} onChange={e => setForm(f => ({ ...f, email_stage: e.target.value }))} placeholder={t('jobs.emailStagePlaceholder')} />
          </div>
          <div className="form-group">
            <label>{t('jobs.reminderEnabled')}</label>
            <select value={form.reminder_enabled ? '1' : '0'} onChange={e => setForm(f => ({ ...f, reminder_enabled: e.target.value === '1' }))}>
              <option value="0">{t('common.no')}</option>
              <option value="1">{t('common.yes')}</option>
            </select>
          </div>
          <div className="form-group">
            <label>{t('jobs.assignedManager')}</label>
            <select value={form.assigned_manager_id} onChange={e => setForm(f => ({ ...f, assigned_manager_id: e.target.value }))}>
              <option value="">{t('jobs.noneCompany')}</option>
              {managers.map(u => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>{t('jobs.handoverManager')}</label>
            <select value={form.handover_manager_id} onChange={e => setForm(f => ({ ...f, handover_manager_id: e.target.value }))}>
              <option value="">{t('jobs.noneCompany')}</option>
              {managers.map(u => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
            </select>
          </div>
          <div className="form-group full-width">
            <label>{t('jobs.meetingHistory')}</label>
            <textarea value={form.meeting_history} onChange={e => setForm(f => ({ ...f, meeting_history: e.target.value }))} rows={2} />
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showStudentStatsModal}
        onClose={() => setShowStudentStatsModal(false)}
        title={`${t('jobs.studentsAppliedSummary', 'Students Applied Summary')} - ${selectedJobForStats?.offer_title || selectedJobForStats?.job_title || ''}`}
        footer={(
          <button className="btn btn-outline" onClick={() => setShowStudentStatsModal(false)}>
            {t('common.close', 'Close')}
          </button>
        )}
      >
        {studentStatsLoading ? (
          <div className="loading-spinner"><div className="spinner"></div></div>
        ) : (
          <div style={{ display: 'grid', gap: 12 }}>
            <div style={{ fontWeight: 700, fontSize: 15 }}>
              {t('jobs.totalAppliedCount', 'Total count: Applied form')} {studentStats.total}
            </div>
            <div style={{ fontWeight: 600, fontSize: 14 }}>
              {t('jobs.statusWiseCount', 'Status wise count')}:
            </div>
            <div style={{ display: 'grid', gap: 8 }}>
              {Object.keys(studentStats.byStatus).length === 0 ? (
                <div style={{ color: 'var(--text-muted)' }}>{t('common.noData', 'No data found')}</div>
              ) : (
                Object.entries(studentStats.byStatus)
                  .sort((a, b) => a[0].localeCompare(b[0]))
                  .map(([status, count]) => (
                    <div key={status} style={{ display: 'flex', justifyContent: 'space-between', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 10px' }}>
                      <span style={{ fontWeight: 600 }}>{status}</span>
                      <span>{count}</span>
                    </div>
                  ))
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default JobList;
